/**
 * feat-138 — Pairing Challenge admin editor page.
 *
 * ONE logical form (left) over the head-to-head model (ADR-0001 D1); TWO live
 * phone-frame previews (right) — One California + 580 California — each showing
 * the derived `challenge.status` blob exactly as the on-device card renders it,
 * so a swapped goal / flipped is_you is caught before save. A history panel
 * shows who saved / when / the persisted blobs (D7).
 *
 * Daily numbers (goals, percent-to-goal, pace) are surfaced prominently;
 * structural fields (campaign copy, colors, avatars) are secondary/collapsible.
 *
 * Reachable ONLY from the Administration "Pairing Challenge" tile, behind the
 * same admin gating. Env-driven target (STAGING for build/QA).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, RefreshCw, ChevronDown, History as HistoryIcon } from 'lucide-react';
import ScorecardNavigation from '../ScorecardNavigation';
import PairingCardPreview from './PairingCardPreview';
import { deriveRow } from './pairingDerivation';
import {
  fetchPairing,
  savePairing,
  fetchPairingHistory,
  PAIRING_SLUG,
  ONE_CALIFORNIA_LOCATION_ID,
  FIVE_EIGHTY_LOCATION_ID,
  type PairingLogical,
  type PairingTeamLogical,
  type PairingHistoryEntry,
  type PaceStatus,
} from '../../../services/pairingChallengeService';

// ── Default template (mirrors ADR D1 / seed 095) — used if GET 404s so the ────
// editor is still usable in an unseeded env. Never overwrites fetched data.
const DEFAULT_LOGICAL: PairingLogical = {
  pairing: PAIRING_SLUG,
  title: "Today's Pairing Goal",
  campaign: { start: '2026-07-06', end: '2026-08-14' },
  bars_title: 'Pairing Offers — % to Goal',
  pace_percent: 68,
  today_goal_caption: 'needed today to stay on pace',
  teams: [
    {
      location_id: ONE_CALIFORNIA_LOCATION_ID,
      name: 'One California',
      percent_to_goal: 64,
      pace_status: 'behind',
      pace_label: 'behind pace',
      bar_color: '#C0122B',
      avatar_url:
        'https://bespoke-public-assets.s3.us-west-1.amazonaws.com/ladle_and_leaf/challenges/peppermascot.png',
      avatar_placeholder: 'pepper',
      yesterday_count: 275,
      today_goal: 295,
      final_goal: 7214,
      cumulative_offers: 0,
    },
    {
      location_id: FIVE_EIGHTY_LOCATION_ID,
      name: '580 California',
      percent_to_goal: 71,
      pace_status: 'ahead',
      pace_label: 'ahead pace',
      bar_color: '#E0A200',
      avatar_url:
        'https://bespoke-public-assets.s3.us-west-1.amazonaws.com/ladle_and_leaf/challenges/avocadomascot.png',
      avatar_placeholder: 'avocado',
      yesterday_count: 262,
      today_goal: 281,
      final_goal: 16120,
      cumulative_offers: 0,
    },
  ],
};

const PACE_OPTIONS: { value: PaceStatus; label: string }[] = [
  { value: 'ahead', label: 'Ahead' },
  { value: 'behind', label: 'Behind' },
  { value: 'on_pace', label: 'On pace' },
];

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const isInt = (n: number) => Number.isFinite(n) && Number.isInteger(n);

// ── Validation (mirrors ADR D5; the backend is authoritative) ─────────────────
type Errors = Record<string, string>;

// Max lengths mirror the backend Pydantic model (confirmed with feat-138 backend)
// so the author sees an inline error instead of a 422.
const MAX = {
  title: 120,
  bars_title: 120,
  name: 120,
  pace_label: 64,
  today_goal_caption: 200,
  avatar_url: 512,
  avatar_placeholder: 64,
};

function validate(m: PairingLogical): Errors {
  const e: Errors = {};
  if (!m.title.trim()) e['title'] = 'Title is required.';
  else if (m.title.length > MAX.title) e['title'] = `Max ${MAX.title} characters.`;
  if (!m.bars_title.trim()) e['bars_title'] = 'Bars title is required.';
  else if (m.bars_title.length > MAX.bars_title) e['bars_title'] = `Max ${MAX.bars_title} characters.`;
  if (m.today_goal_caption.length > MAX.today_goal_caption)
    e['today_goal_caption'] = `Max ${MAX.today_goal_caption} characters.`;
  if (!DATE_RE.test(m.campaign.start)) e['start'] = 'Start must be YYYY-MM-DD.';
  if (!DATE_RE.test(m.campaign.end)) e['end'] = 'End must be YYYY-MM-DD.';
  if (!isInt(m.pace_percent) || m.pace_percent < 0 || m.pace_percent > 100)
    e['pace_percent'] = 'Pace % must be an integer 0–100.';
  m.teams.forEach((t, i) => {
    if (!t.name.trim()) e[`name_${i}`] = 'Name is required.';
    else if (t.name.length > MAX.name) e[`name_${i}`] = `Max ${MAX.name} characters.`;
    if (!isInt(t.percent_to_goal) || t.percent_to_goal < 0)
      e[`percent_to_goal_${i}`] = 'Must be an integer ≥ 0.';
    else if (t.percent_to_goal > 1000) e[`percent_to_goal_${i}`] = 'That looks too high (≤ 1000).';
    if (!isInt(t.yesterday_count) || t.yesterday_count < 0)
      e[`yesterday_count_${i}`] = 'Must be an integer ≥ 0.';
    if (!isInt(t.today_goal) || t.today_goal < 0)
      e[`today_goal_${i}`] = 'Must be an integer ≥ 0.';
    if (!isInt(t.final_goal) || t.final_goal < 0)
      e[`final_goal_${i}`] = 'Must be an integer ≥ 0.';
    if (t.pace_label.length > MAX.pace_label) e[`pace_label_${i}`] = `Max ${MAX.pace_label} characters.`;
    if (!HEX_RE.test(t.bar_color)) e[`bar_color_${i}`] = 'Must be a #RRGGBB hex color.';
    // Backend requires a well-formed https:// URL (the on-device emoji fallback
    // only covers load failures, not a missing url in the blob).
    if (!/^https:\/\//i.test(t.avatar_url.trim())) e[`avatar_url_${i}`] = 'Must be an https:// URL.';
    else if (t.avatar_url.length > MAX.avatar_url) e[`avatar_url_${i}`] = `Max ${MAX.avatar_url} characters.`;
    if (t.avatar_placeholder.length > MAX.avatar_placeholder)
      e[`avatar_placeholder_${i}`] = `Max ${MAX.avatar_placeholder} characters.`;
  });
  return e;
}

/** Replace NaN numbers with 0 so the preview never renders "NaN" while editing. */
function sanitizeForPreview(m: PairingLogical): PairingLogical {
  const n = (v: number) => (Number.isFinite(v) ? v : 0);
  return {
    ...m,
    pace_percent: n(m.pace_percent),
    teams: m.teams.map((t) => ({
      ...t,
      percent_to_goal: n(t.percent_to_goal),
      yesterday_count: n(t.yesterday_count),
      today_goal: n(t.today_goal),
      final_goal: n(t.final_goal),
      cumulative_offers: n(t.cumulative_offers),
    })) as PairingTeamLogical[],
  };
}

// ── Small form primitives ─────────────────────────────────────────────────────
const Label: React.FC<{ children: React.ReactNode; htmlFor?: string }> = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-xs font-semibold text-inkMuted mb-1">
    {children}
  </label>
);

const ErrText: React.FC<{ msg?: string }> = ({ msg }) =>
  msg ? <p className="mt-1 text-xs text-red-600">{msg}</p> : null;

/**
 * feat-227 — every field below is STILL COLLECTED, STILL VALIDATED, AND STILL SENT.
 * D1/D2 retire nothing: the feat-140 recalc keeps re-deriving these daily and the
 * backend write model still requires them, so the form is unchanged. What changed is
 * the CARD — it stopped drawing them (request §4.1/§4.2). Without a note saying so,
 * an author edits "Previous Day", watches the preview and the phone ignore it, and
 * has no way to tell a hidden field from a broken save. That confusion is guaranteed
 * otherwise, and it costs one label.
 */
const STORED_NOT_SHOWN = 'Stored, not shown on the app';

const FieldNote: React.FC<{ text?: string }> = ({ text }) =>
  text ? <p className="-mt-1 mb-1 text-[11px] leading-tight text-inkMuted/80">{text}</p> : null;

const inputCls =
  'w-full rounded-lg border border-softBorder px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-coral/40';

const TextField: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  placeholder?: string;
  type?: string;
  hint?: string;
}> = ({ id, label, value, onChange, error, placeholder, type = 'text', hint }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <FieldNote text={hint} />
    <input
      id={id}
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls}
    />
    <ErrText msg={error} />
  </div>
);

const NumberField: React.FC<{
  id: string;
  label: string;
  value: number;
  onChange?: (v: number) => void;
  error?: string;
  min?: number;
  max?: number;
  emphasize?: boolean;
  readOnly?: boolean;
  hint?: string;
}> = ({ id, label, value, onChange, error, min, max, emphasize, readOnly, hint }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <FieldNote text={hint} />
    <input
      id={id}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      readOnly={readOnly}
      value={Number.isFinite(value) ? String(value) : ''}
      onChange={(e) => onChange?.(e.target.value === '' ? NaN : Number(e.target.value))}
      className={`${inputCls} ${emphasize ? 'text-lg font-semibold' : ''} ${
        readOnly ? 'bg-cream/50 text-inkMuted cursor-not-allowed' : ''
      }`}
    />
    <ErrText msg={error} />
  </div>
);

const SelectField: React.FC<{
  id: string;
  label: string;
  value: PaceStatus;
  onChange: (v: PaceStatus) => void;
  hint?: string;
}> = ({ id, label, value, onChange, hint }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <FieldNote text={hint} />
    <select id={id} value={value} onChange={(e) => onChange(e.target.value as PaceStatus)} className={inputCls}>
      {PACE_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const Section: React.FC<{
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="bg-white border border-softBorder rounded-xl shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3"
      >
        <span className="text-sm font-display font-semibold text-ink">{title}</span>
        <ChevronDown
          size={16}
          className={`text-inkMuted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
};

// ── Team names by index (labels only; ids/order are fixed) ────────────────────
const TEAM_LABELS = ['One California', '580 California'];

// ── History panel ─────────────────────────────────────────────────────────────
const HistoryPanel: React.FC<{ entries: PairingHistoryEntry[]; loading: boolean }> = ({
  entries,
  loading,
}) => {
  const [expanded, setExpanded] = React.useState<number | null>(null);
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };
  return (
    <div className="bg-white border border-softBorder rounded-xl shadow-sm">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-softBorder">
        <HistoryIcon size={16} className="text-inkMuted" />
        <span className="text-sm font-display font-semibold text-ink">Save history</span>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <p className="px-4 py-6 text-sm text-inkMuted">Loading history…</p>
        ) : entries.length === 0 ? (
          <p className="px-4 py-6 text-sm text-inkMuted">No saves recorded yet.</p>
        ) : (
          <ul className="divide-y divide-softBorder">
            {entries.map((h, i) => (
              <li key={`${h.run_at}-${i}`} className="px-4 py-3">
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === i ? null : i)}
                  className="w-full text-left"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-ink">{h.triggered_by || 'unknown'}</span>
                    <span className="text-xs text-inkMuted">{fmt(h.run_at)}</span>
                  </div>
                  {h.challenge_id && (
                    <div className="text-xs text-inkMuted mt-0.5">challenge {h.challenge_id}</div>
                  )}
                </button>
                {expanded === i && (
                  <pre className="mt-2 max-h-64 overflow-auto rounded bg-cream p-2 text-[11px] leading-tight text-ink">
                    {JSON.stringify(h.status_json, null, 2)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ── Phone frame wrapper ───────────────────────────────────────────────────────
const PhoneFrame: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col items-center">
    <span className="mb-2 text-xs font-semibold text-inkMuted">{label}</span>
    <div className="w-full max-w-[340px] rounded-[2rem] border-[6px] border-ink/80 bg-[#FFFEF9] p-3 shadow-lg">
      {children}
    </div>
  </div>
);

// ── The editor ────────────────────────────────────────────────────────────────
const PairingChallengeEditor: React.FC = () => {
  const navigate = useNavigate();
  const [model, setModel] = React.useState<PairingLogical | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [notSeeded, setNotSeeded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [history, setHistory] = React.useState<PairingHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(true);

  const loadHistory = React.useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistory(await fetchPairingHistory());
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setNotSeeded(false);
    try {
      const fetched = await fetchPairing();
      if (fetched && Array.isArray(fetched.teams) && fetched.teams.length === 2) {
        // Pin canonical order (One California first) regardless of server order.
        const ordered = [...fetched.teams].sort((a, b) =>
          a.location_id === ONE_CALIFORNIA_LOCATION_ID ? -1 : b.location_id === ONE_CALIFORNIA_LOCATION_ID ? 1 : 0,
        );
        setModel({ ...fetched, pairing: PAIRING_SLUG, teams: ordered });
      } else {
        // 404 / unseeded env — start from the default template (D4: uninitialized).
        setNotSeeded(true);
        setModel(structuredClone(DEFAULT_LOGICAL));
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load pairing.');
      setModel(structuredClone(DEFAULT_LOGICAL));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    loadHistory();
  }, [load, loadHistory]);

  const errors = React.useMemo(() => (model ? validate(model) : {}), [model]);
  const hasErrors = Object.keys(errors).length > 0;

  // Mutators keep the two fixed teams by index; ids/order never change.
  const setShared = <K extends keyof PairingLogical>(key: K, value: PairingLogical[K]) =>
    setModel((m) => (m ? { ...m, [key]: value } : m));
  const setCampaign = (key: 'start' | 'end', value: string) =>
    setModel((m) => (m ? { ...m, campaign: { ...m.campaign, [key]: value } } : m));
  const setTeam = <K extends keyof PairingTeamLogical>(i: number, key: K, value: PairingTeamLogical[K]) =>
    setModel((m) =>
      m ? { ...m, teams: m.teams.map((t, idx) => (idx === i ? { ...t, [key]: value } : t)) } : m,
    );

  const handleSave = async () => {
    if (!model || hasErrors) return;
    setSaving(true);
    setToast(null);
    try {
      const saved = await savePairing(model);
      const ordered = [...saved.teams].sort((a, b) =>
        a.location_id === ONE_CALIFORNIA_LOCATION_ID ? -1 : b.location_id === ONE_CALIFORNIA_LOCATION_ID ? 1 : 0,
      );
      setModel({ ...saved, pairing: PAIRING_SLUG, teams: ordered });
      setNotSeeded(false);
      setToast({ kind: 'ok', msg: 'Saved — both cards updated.' });
      loadHistory();
    } catch (err) {
      setToast({ kind: 'err', msg: err instanceof Error ? err.message : 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const previewModel = model ? sanitizeForPreview(model) : null;

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <button
              onClick={() => navigate('/restaurant/administration')}
              className="inline-flex items-center gap-1 text-sm text-inkMuted hover:text-ink mb-1"
            >
              <ArrowLeft size={14} /> Administration
            </button>
            <h1 className="text-3xl font-display font-semibold text-ink">Pairing Challenge</h1>
            <p className="text-sm text-inkMuted">
              Edit the head-to-head daily numbers. Both location cards update from one form.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading || saving}
              className="inline-flex items-center gap-1 rounded-lg border border-softBorder bg-white px-3 py-2 text-sm text-ink hover:bg-cream disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Reload
            </button>
            <button
              onClick={handleSave}
              disabled={loading || saving || hasErrors || !model}
              className="inline-flex items-center gap-1 rounded-lg bg-coral px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-coral/90 disabled:opacity-50"
            >
              <Save size={14} /> {saving ? 'Saving…' : 'Save both cards'}
            </button>
          </div>
        </div>

        {/* Banners */}
        {toast && (
          <div
            className={`mb-4 rounded-lg px-4 py-3 text-sm ${
              toast.kind === 'ok'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {toast.msg}
          </div>
        )}
        {loadError && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            Could not load current values: {loadError}. Showing a default template — verify before saving.
          </div>
        )}
        {notSeeded && !loadError && (
          <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
            No existing pairing found in this environment. You're starting from a default template; saving
            will write both rows.
          </div>
        )}
        {hasErrors && !loading && (
          <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
            Fix the highlighted fields before saving.
          </div>
        )}

        {loading || !model || !previewModel ? (
          <div className="py-24 text-center text-inkMuted">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT — the single logical form */}
            <div className="space-y-4">
              {/* Daily numbers — prominent, always open */}
              <Section title="Daily numbers" defaultOpen>
                <NumberField
                  id="pace_percent"
                  label="Pace line position (% — shared, both cards)"
                  hint={`${STORED_NOT_SHOWN} — the dashed pace line was removed from the card.`}
                  value={model.pace_percent}
                  onChange={(v) => setShared('pace_percent', v)}
                  error={errors['pace_percent']}
                  min={0}
                  max={100}
                  emphasize
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                  {model.teams.map((t, i) => (
                    <div key={t.location_id} className="rounded-lg bg-cream/60 p-3 space-y-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-full"
                          style={{ background: HEX_RE.test(t.bar_color) ? t.bar_color : '#B0B6BF' }}
                        />
                        <span className="text-sm font-display font-semibold text-ink">{TEAM_LABELS[i]}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <NumberField
                          id={`today_goal_${i}`}
                          label="Today's goal"
                          hint={STORED_NOT_SHOWN}
                          value={t.today_goal}
                          onChange={(v) => setTeam(i, 'today_goal', v)}
                          error={errors[`today_goal_${i}`]}
                          min={0}
                          emphasize
                        />
                        <NumberField
                          id={`yesterday_count_${i}`}
                          label="Previous Day"
                          hint={STORED_NOT_SHOWN}
                          value={t.yesterday_count}
                          onChange={(v) => setTeam(i, 'yesterday_count', v)}
                          error={errors[`yesterday_count_${i}`]}
                          min={0}
                          emphasize
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <NumberField
                          id={`final_goal_${i}`}
                          label="6-week goal (final)"
                          value={t.final_goal}
                          onChange={(v) => setTeam(i, 'final_goal', v)}
                          error={errors[`final_goal_${i}`]}
                          min={0}
                          emphasize
                        />
                        <NumberField
                          id={`cumulative_offers_${i}`}
                          label="Cumulative offers (recalc)"
                          value={t.cumulative_offers}
                          min={0}
                          readOnly
                        />
                      </div>
                      <NumberField
                        id={`percent_to_goal_${i}`}
                        label="Percent to goal (%)"
                        hint="Shown on the app — this is the number on the bar."
                        value={t.percent_to_goal}
                        onChange={(v) => setTeam(i, 'percent_to_goal', v)}
                        error={errors[`percent_to_goal_${i}`]}
                        min={0}
                        emphasize
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <SelectField
                          id={`pace_status_${i}`}
                          label="Pace status"
                          hint={STORED_NOT_SHOWN}
                          value={t.pace_status}
                          onChange={(v) => setTeam(i, 'pace_status', v)}
                        />
                        <TextField
                          id={`pace_label_${i}`}
                          label="Pace label"
                          hint={`${STORED_NOT_SHOWN} — the pace pills were removed.`}
                          value={t.pace_label}
                          onChange={(v) => setTeam(i, 'pace_label', v)}
                          error={errors[`pace_label_${i}`]}
                          placeholder="e.g. behind pace"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Campaign & copy — secondary */}
              <Section title="Campaign & copy" defaultOpen={false}>
                <TextField
                  id="title"
                  label="Title (shared)"
                  hint="Shown on the app — this is the card's heading, rendered exactly as typed."
                  value={model.title}
                  onChange={(v) => setShared('title', v)}
                  error={errors['title']}
                />
                <div className="grid grid-cols-2 gap-3">
                  <TextField
                    id="start"
                    label="Campaign start"
                    type="date"
                    value={model.campaign.start}
                    onChange={(v) => setCampaign('start', v)}
                    error={errors['start']}
                  />
                  <TextField
                    id="end"
                    label="Campaign end"
                    type="date"
                    value={model.campaign.end}
                    onChange={(v) => setCampaign('end', v)}
                    error={errors['end']}
                  />
                </div>
                <TextField
                  id="bars_title"
                  label="Bars section title (shared)"
                  hint={`${STORED_NOT_SHOWN} — the card now uses Title above the bars.`}
                  value={model.bars_title}
                  onChange={(v) => setShared('bars_title', v)}
                  error={errors['bars_title']}
                />
                <TextField
                  id="today_goal_caption"
                  label="Caption under goal (shared)"
                  hint={`${STORED_NOT_SHOWN} — it sat under the removed goal box.`}
                  value={model.today_goal_caption}
                  onChange={(v) => setShared('today_goal_caption', v)}
                  error={errors['today_goal_caption']}
                  placeholder="needed today to stay on pace"
                />
              </Section>

              {/* Team appearance — secondary/structural */}
              <Section title="Team appearance (colors & mascots)" defaultOpen={false}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {model.teams.map((t, i) => (
                    <div key={t.location_id} className="rounded-lg bg-cream/60 p-3 space-y-3">
                      <span className="text-sm font-display font-semibold text-ink">{TEAM_LABELS[i]}</span>
                      <TextField
                        id={`name_${i}`}
                        label="Display name"
                        value={t.name}
                        onChange={(v) => setTeam(i, 'name', v)}
                        error={errors[`name_${i}`]}
                      />
                      <div>
                        <Label htmlFor={`bar_color_${i}`}>Bar color (#RRGGBB)</Label>
                        <div className="flex items-center gap-2">
                          <input
                            aria-label={`${TEAM_LABELS[i]} bar color swatch`}
                            type="color"
                            value={HEX_RE.test(t.bar_color) ? t.bar_color : '#B0B6BF'}
                            onChange={(e) => setTeam(i, 'bar_color', e.target.value.toUpperCase())}
                            className="h-9 w-10 rounded border border-softBorder bg-white p-0.5"
                          />
                          <input
                            id={`bar_color_${i}`}
                            type="text"
                            value={t.bar_color}
                            onChange={(e) => setTeam(i, 'bar_color', e.target.value)}
                            className={inputCls}
                          />
                        </div>
                        <ErrText msg={errors[`bar_color_${i}`]} />
                      </div>
                      <TextField
                        id={`avatar_url_${i}`}
                        label="Avatar image URL"
                        value={t.avatar_url}
                        onChange={(v) => setTeam(i, 'avatar_url', v)}
                        error={errors[`avatar_url_${i}`]}
                      />
                      <TextField
                        id={`avatar_placeholder_${i}`}
                        label="Emoji fallback key (pepper, avocado, baguette, mushroom)"
                        value={t.avatar_placeholder}
                        onChange={(v) => setTeam(i, 'avatar_placeholder', v)}
                        error={errors[`avatar_placeholder_${i}`]}
                      />
                    </div>
                  ))}
                </div>
              </Section>
            </div>

            {/* RIGHT — dual live previews + history */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <PhoneFrame label="One California (device)">
                  <PairingCardPreview row={deriveRow(previewModel, 0)} />
                </PhoneFrame>
                <PhoneFrame label="580 California (device)">
                  <PairingCardPreview row={deriveRow(previewModel, 1)} />
                </PhoneFrame>
              </div>
              <p className="text-xs text-inkMuted text-center">
                Each card shows that location's own device view — its own goal, and{' '}
                <span className="font-semibold">(you)</span> next to its own team. A swapped goal shows here
                before you save.
              </p>
              <HistoryPanel entries={history} loading={historyLoading} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PairingChallengeEditor;
